import React, { useState } from 'react';
import './RecommendationCards.css';
import { ClubRecommendation } from '../utils/interfaces';

interface RecommendationCardsProps {
  recommendations: ClubRecommendation[];
  compare?: string;
}

// Utility function to safely display price
const formatPrice = (price: string | undefined | null): string => {
  if (!price || typeof price !== 'string') return '';
  return price;
};

// Utility function to get retailer-specific button styling
const getRetailerButtonStyle = (retailer: string) => {
  const baseStyle = {
    padding: '8px 12px',
    border: 'none',
    borderRadius: '6px',
    fontWeight: '500',
    fontSize: '0.9rem',
    textDecoration: 'none',
    display: 'inline-block',
    textAlign: 'center' as const,
    transition: 'all 0.2s ease',
    minWidth: '80px',
    color: '#fff',
    whiteSpace: 'normal' as const,
    wordWrap: 'break-word' as const,
    lineHeight: '1.2'
  };

  switch (retailer) {
    case 'eBay':
      return { ...baseStyle, backgroundColor: '#e53238', ':hover': { backgroundColor: '#c41e3a' } };
    case 'PGA Tour Superstore':
      return { ...baseStyle, backgroundColor: '#1f4e79', ':hover': { backgroundColor: '#174063' } };
    case 'Global Golf':
      return { ...baseStyle, backgroundColor: '#228b22', ':hover': { backgroundColor: '#1e7a1e' } };
    case '2nd Swing':
      return { ...baseStyle, backgroundColor: '#e07a5f', ':hover': { backgroundColor: '#cc6a51' } };
    default:
      return { ...baseStyle, backgroundColor: '#1976d2', ':hover': { backgroundColor: '#1565c0' } };
  }
};

// Component for handling individual card images with loading states
const CardImage: React.FC<{ src?: string; alt: string; model: string }> = ({ src, alt, model }) => {
  const [loading, setLoading] = useState(!!src);
  const [error, setError] = useState(false);

  const handleImageLoad = () => {
    setLoading(false);
  };

  const handleImageError = () => {
    setLoading(false);
    setError(true);
  };

  return (
    <div className="card-image-wrapper">
      {loading && src && (
        <div className="card-image-loading">Loading image...</div>
      )}
      
      {!src || error ? (
        <div className="card-image-placeholder">
          <div className="placeholder-icon">📷</div>
          <div className="placeholder-text">No image available</div>
        </div>
      ) : (
        <img
          src={src}
          alt={alt}
          className="card-image"
          onLoad={handleImageLoad}
          onError={handleImageError}
          style={{
            opacity: loading ? 0 : 1,
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            background: '#f8f8f8'
          }}
        />
      )}
    </div>
  );
};

const RecommendationCards: React.FC<RecommendationCardsProps> = ({ recommendations, compare }) => (
  <>
    <div className="recommendations-grid">
      {recommendations.map((rec, index) => (
        <div key={index} className="recommendation-card">
          {index < 3 && (
            <div className={`rank-badge rank-${index + 1}`}>
              {index === 0 ? 'Best' : `${index + 1}${index === 1 ? 'nd' : 'rd'}`}
            </div>
          )}
          <CardImage src={rec.image} alt={rec.model} model={rec.model} />
          <div className="card-header">
            <h4 className="card-model">{rec.model}</h4>
            <p className="card-price">{formatPrice(rec.price)}</p>
          </div>
          <div className="card-content">
            <p className="card-reason"><strong>Reason:</strong> {rec.reason}</p>
            <div className="card-details">
              <p><strong>Shaft:</strong> {rec.shaft}</p>
            </div>
            <h5>Key Benefits:</h5>
            <ul className="card-benefits">
              {rec.benefits?.map((benefit, i) => (
                <li key={i}>{benefit}</li>
              ))}
            </ul>
            {rec.con && rec.con.length > 0 && (
              <>
                <h5>Considerations:</h5>
                <ul className="card-cons">
                  {rec.con.map((con, i) => (
                    <li key={i}>&nbsp;{con}</li>
                  ))}
                </ul>
              </>
            )}
          </div>
                    <div className="card-footer">
            {/* Display retailer buttons from links array */}
            <div className="retailer-links" style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'center' }}>
              {rec.links && rec.links.length > 0 ? (
                rec.links.map((retailerLink, linkIndex) => (
                  <a
                    key={linkIndex}
                    href={retailerLink.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={getRetailerButtonStyle(retailerLink.retailer)}
                    onMouseOver={(e) => {
                      const styles = getRetailerButtonStyle(retailerLink.retailer);
                      if (styles[':hover']) {
                        e.currentTarget.style.backgroundColor = styles[':hover'].backgroundColor;
                      }
                    }}
                    onMouseOut={(e) => {
                      const styles = getRetailerButtonStyle(retailerLink.retailer);
                      e.currentTarget.style.backgroundColor = styles.backgroundColor;
                    }}
                    title={`View on ${retailerLink.retailer}`}
                  >
                    {retailerLink.retailer === 'PGA Tour Superstore' ? (
                      <>View on PGA Tour<br />Superstore</>
                    ) : retailerLink.retailer === 'eBay' ? (
                      'View on eBay'
                    ) : retailerLink.retailer === '2nd Swing' ? (
                      'View on 2nd Swing'
                    ) : retailerLink.retailer === 'Global Golf' ? (
                      'View on Global Golf'
                    ) : (
                      'View Deal'
                    )}
                  </a>
                ))
              ) : (
                <span style={{ color: '#999', fontSize: '0.9rem' }}>No purchase links available</span>
              )}
            </div>
          </div>
        </div>
      ))}
    {compare && (
      <div className="recommendation-card">
        <div className="compare-card">
          <h3>Comparison</h3>
          <p>{compare}</p>
          <p className="commission-disclaimer extra-small-text">We may earn a commission through purchases made with these links at no additional cost to you. Prices may vary depending on the seller.</p>
        </div>
      </div>
    )}
    </div>
  </>
);

export default RecommendationCards;
